// Helper function to format CSS variable names into readable display names
export const formatCssVariableName = (cssVar: string): string => {
  // Extract variable name from var(--variable-name) format
  const varMatch = cssVar.match(/var\(--([^)]+)\)/);
  if (!varMatch) return cssVar;

  const varName = varMatch[1];

  // Convert kebab-case to Title Case
  // e.g., "secondary-n-900" -> "Secondary N 900"
  return varName
    .split("-")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
};

// Helper function to recursively resolve CSS custom properties
export const recursivelyResolveCssVariable = (
  value: string,
  cssCustomProperties: Record<string, string> | null,
  depth = 0,
): string => {
  // Prevent infinite recursion
  if (depth > 10) return value;

  // If it's not a CSS variable or we don't have CSS custom properties, return as-is
  if (!value.includes("var(--") || !cssCustomProperties) return value;

  // Extract the variable name from var(--variable-name)
  const varMatch = value.match(/var\(--([^)]+)\)/);
  if (!varMatch) return value;

  const varName = `--${varMatch[1]}`;
  const resolvedValue = cssCustomProperties[varName];

  // If we can't resolve it, return as-is
  if (!resolvedValue) return value;

  // If the resolved value is also a CSS variable, recursively resolve it
  if (resolvedValue.includes("var(--")) {
    return recursivelyResolveCssVariable(
      resolvedValue,
      cssCustomProperties,
      depth + 1,
    );
  }

  // Handle implicit HSL values - if the resolved value contains % symbols
  // and isn't already a formatted color (hex, hsl, hsv, rgb), wrap it in hsl()
  if (
    resolvedValue.includes("%") &&
    !resolvedValue.startsWith("#") &&
    !resolvedValue.startsWith("hsl") &&
    !resolvedValue.startsWith("hsv") &&
    !resolvedValue.startsWith("rgb")
  ) {
    const hslParts = resolvedValue.split(/\s+/);

    if (hslParts.length >= 3) {
      const h = hslParts[0];
      const s = hslParts[1].endsWith("%") ? hslParts[1] : `${hslParts[1]}%`;
      const l = hslParts[2].endsWith("%") ? hslParts[2] : `${hslParts[2]}%`;
      return `hsl(${h}, ${s}, ${l})`;
    }
  }

  // Otherwise, return the resolved value
  return resolvedValue;
};

// Helper function to recursively resolve all CSS custom properties in a record
export const resolveAllCssCustomProperties = (
  cssCustomProperties: Record<string, string>,
): Record<string, string> => {
  const resolved: Record<string, string> = {};

  // First pass: copy all values
  for (const [key, value] of Object.entries(cssCustomProperties)) {
    resolved[key] = value;
  }

  // Second pass: resolve all variables
  for (const [key, value] of Object.entries(cssCustomProperties)) {
    resolved[key] = recursivelyResolveCssVariable(value, resolved);
  }

  return resolved;
};

export const extractApplicationStoryboardUrl = (
  fullUrl: string,
  devServerUrl: string,
) => {
  const url = new URL(fullUrl);
  // remove search params added in useStoryboardUrl
  url.searchParams.delete("debugLog");
  url.searchParams.delete("framework");
  const devServerUrlObj = new URL(devServerUrl);
  if (url.origin !== devServerUrlObj.origin) {
    return "/";
  }
  return url.href.slice(url.origin.length);
};
